import { computed, ref } from 'vue'

import { useConfigStore } from '../../stores/config'
import { adSlotsConfigurationSchema } from '../types/ad-slot-types'
import { useDeviceType } from './use-device-type'
import { useDisallowAds } from './use-disallow-ads'

import type { AdSlotType } from '../types/ad-slot-types'

const registeredBannerAdSlots = ref<string[]>([])
const registeredOutstreamAdSlots = ref<string[]>([])
const registeredRectangleAdSlots = ref<string[]>([])
const registeredNativeAdSlots = ref<string[]>([])
const registeredFooterAdSlots = ref<string[]>([])
const registeredHeaderAdSlots = ref<string[]>([])
const registeredSideAdSlots = ref<string[]>([])

const isLargeDesktop = computed(() => {
  if (typeof window === 'undefined') {
    return null
  }
  return window.innerWidth >= 1240
})

/**
 * Composable for registering ad slots
 * @returns (type: AdSlotType) => string - The registered ad slot name
 */
export function useRegisterAdSlot () {
  const adSlots = useAdSlots()
  const disallowAds = useDisallowAds()

  const register = (type: AdSlotType, index: number) => {
    if (!adSlots.value) {
      // throw new Error('Cannot register ad slot, ad slots configuration not found')
      return null
    }

    if (
      disallowAds.value.disallowAds &&
      (type === 'banner' ||
        type === 'outstream' ||
        type === 'rectangle' ||
        type === 'footer' ||
        type === 'header' ||
        type === 'side')
    ) {
      // if `disallowAds` is true:
      // do not register any display or OutStream slots and hide all Ad Wrappers;
      // Native Teasers & Taboola stay on
      return null
    }

    if (disallowAds.value.disallowOutstream && type === 'outstream') {
      // if `disallowOutstream` is true:
      // do not register or place OutStream slot
      return null
    }

    if (disallowAds.value.disallowNativeTeasers && type === 'native') {
      // if `disallowNativeTeasers` is true:
      // do not place any Native Teasers (Overview & Article)
      // or Related Articles blocks (Article only)
      return null
    }

    switch (type) {
      case 'banner': {
        const maxBannerAdSlots = adSlots.value.dynamicBannerAdSlots.length
        if (index < 0 || index >= maxBannerAdSlots) {
          // throw new Error('Cannot register ad slot, invalid banner ad slot index')
          return null
        }
        if (registeredBannerAdSlots.value[index] !== undefined) {
          // throw new Error('Ad slot already registered at this index')
          return null
        }
        const slotName = adSlots.value.dynamicBannerAdSlots[index]
        if (!slotName) {
          // throw new Error('Cannot register ad slot, slot name not found')
          return null
        }
        registeredBannerAdSlots.value[index] = slotName
        return slotName
      }
      case 'outstream': {
        if (registeredOutstreamAdSlots.value[index] !== undefined) {
          // throw new Error('Ad slot already registered at this index')
          return null
        }
        // Always use the first outstream ad slot
        const slotName = adSlots.value.dynamicOutstreamAdSlots[0]
        if (!slotName) {
          // throw new Error('Cannot register ad slot, slot name not found')
          return null
        }
        registeredOutstreamAdSlots.value[index] = slotName
        return slotName
      }
      case 'rectangle': {
        const maxRectangleAdSlots =
          adSlots.value.dynamicRectangleAdSlots.length
        if (index < 0 || index >= maxRectangleAdSlots) {
          // throw new Error('Cannot register ad slot, invalid rectangle ad slot index')
          return null
        }
        if (registeredRectangleAdSlots.value[index] !== undefined) {
          // throw new Error('Ad slot already registered at this index')
          return null
        }
        const slotName = adSlots.value.dynamicRectangleAdSlots[index]
        if (!slotName) {
          // throw new Error('Cannot register ad slot, slot name not found')
          return null
        }
        registeredRectangleAdSlots.value[index] = slotName
        return slotName
      }
      case 'native': {
        const maxNativeAdSlots = adSlots.value.nativeAdSlots.length
        if (index < 0 || index >= maxNativeAdSlots) {
          // throw new Error('Cannot register ad slot, invalid native ad slot index')
          return null
        }
        if (registeredNativeAdSlots.value[index] !== undefined) {
          // throw new Error('Ad slot already registered at this index')
          return null
        }
        const slotName = adSlots.value.nativeAdSlots[index]
        if (!slotName) {
          // throw new Error('Cannot register ad slot, slot name not found')
          return null
        }
        registeredNativeAdSlots.value[index] = slotName
        return slotName
      }
      case 'footer': {
        const maxFooterAdSlots = adSlots.value.staticFooterAdSlots.length
        if (index < 0 || index >= maxFooterAdSlots) {
          // throw new Error('Cannot register ad slot, invalid footer ad slot index')
          return null
        }
        if (registeredFooterAdSlots.value[index] !== undefined) {
          // throw new Error('Ad slot already registered at this index')
          return null
        }
        const slotName = adSlots.value.staticFooterAdSlots[index]
        if (!slotName) {
          // throw new Error('Cannot register ad slot, slot name not found')
          return null
        }
        registeredFooterAdSlots.value[index] = slotName
        return slotName
      }
      case 'header': {
        const maxHeaderAdSlots = adSlots.value.staticHeaderAdSlots.length
        if (index < 0 || index >= maxHeaderAdSlots) {
          // throw new Error('Cannot register ad slot, invalid header ad slot index')
          return null
        }
        if (registeredHeaderAdSlots.value[index] !== undefined) {
          // throw new Error('Ad slot already registered at this index')
          return null
        }
        const slotName = adSlots.value.staticHeaderAdSlots[index]
        if (!slotName) {
          // throw new Error('Cannot register ad slot, slot name not found')
          return null
        }
        registeredHeaderAdSlots.value[index] = slotName
        return slotName
      }
      case 'side': {
        const maxSideAdSlots = adSlots.value.staticSideAdSlots.length
        if (index < 0 || index >= maxSideAdSlots) {
          // throw new Error('Cannot register ad slot, invalid side ad slot index')
          return null
        }
        if (registeredSideAdSlots.value[index] !== undefined) {
          // throw new Error('Ad slot already registered at this index')
          return null
        }
        const slotName = adSlots.value.staticSideAdSlots[index]
        if (!slotName) {
          // throw new Error('Cannot register ad slot, slot name not found')
          return null
        }
        if (!isLargeDesktop.value) {
          // throw new Error('Cannot register ad slot, side ad slot not available on this device type')
          return null
        }
        registeredSideAdSlots.value[index] = slotName
        return slotName
      }
      default: {
        // throw new Error(`Invalid ad slot type: ${type}`)
        return null
      }
    }
  }

  const registeredAdSlots = computed(() => {
    return [
      ...registeredBannerAdSlots.value,
      ...registeredOutstreamAdSlots.value,
      ...registeredRectangleAdSlots.value,
      ...registeredNativeAdSlots.value,
      ...registeredFooterAdSlots.value,
      ...registeredHeaderAdSlots.value,
      ...registeredSideAdSlots.value
    ].filter(Boolean)
  })

  const totalAdsRegistered = computed(() => {
    return (
      registeredBannerAdSlots.value.length +
      registeredOutstreamAdSlots.value.length +
      registeredRectangleAdSlots.value.length +
      registeredNativeAdSlots.value.length +
      registeredFooterAdSlots.value.length +
      registeredHeaderAdSlots.value.length +
      registeredSideAdSlots.value.length
    )
  })

  return { register, registeredAdSlots, totalAdsRegistered }
}

/**
 * Composable for getting the ad slots configuration for the current device type
 * @returns ComputedRef<AdSlotSchema | null> - The ad slots configuration for the current device type, null if server side
 */
export function useAdSlots () {
  const configStore = useConfigStore()
  const adSlotsConfig = computed(() => {
    const parsedConfig = adSlotsConfigurationSchema.safeParse(
      configStore.rsConfig.adSlots
    )
    if (!parsedConfig.success) {
      console.error(parsedConfig.error)
      throw new Error('Invalid ad slots configuration')
    }

    return parsedConfig.data
  })

  const deviceType = useDeviceType()

  return computed(() => {
    if (!deviceType.value) {
      return null
    }

    return adSlotsConfig.value[deviceType.value]
  })
}
